import { lazy, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AssetPreview } from '../AssetPreview.js';
import { ImageOverlayDiff } from './ImageOverlayDiff.js';
import { JsonPropertyDiff } from './JsonPropertyDiff.js';
import { MaterialParamDiff } from './MaterialParamDiff.js';
import { MeshDiff } from './MeshDiff.js';
import { TextDiff } from './TextDiff.js';

// MaterialGraphDiff shares the @xyflow/react chunk with MaterialGraph;
// lazy-loading it here means the diff path only pays the cost when
// the user actually opens the Graph tab of a uasset properties diff.
// Rollup deduplicates so both lazy chunks point at the same module.
const MaterialGraphDiff = lazy(() =>
  import('./MaterialGraphDiff.js').then((m) => ({ default: m.MaterialGraphDiff })),
);

// BlueprintGraphDiff also rides the shared @xyflow/react chunk; lazy so
// the diff path only pulls it when comparing a blueprint asset.
const BlueprintGraphDiff = lazy(() =>
  import('./BlueprintGraphDiff.js').then((m) => ({ default: m.BlueprintGraphDiff })),
);

interface RevisionDiffProps {
  depotPath: string;
  headRev: number;
  compareRev: number;
  filename: string;
  fileSize: number | null;
  isText: boolean;
  isDamPreviewable: boolean;
  onClose: () => void;
}

const IMAGE_EXTS = new Set([
  'png', 'jpg', 'jpeg', 'webp', 'gif', 'tif', 'tiff', 'svg', 'avif',
  'heic', 'heif', 'tga', 'bmp', 'psd', 'psb', 'ico', 'dds', 'exr', 'hdr',
]);
const THREED_EXTS = new Set(['fbx', 'obj', 'gltf', 'glb', 'stl', 'dae', 'ply']);

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

export function RevisionDiff({
  depotPath,
  headRev,
  compareRev,
  filename,
  fileSize,
  isText,
  isDamPreviewable,
  onClose,
}: RevisionDiffProps) {
  const left = Math.min(headRev, compareRev);
  const right = Math.max(headRev, compareRev);
  const ext = extOf(filename);

  const header = (
    <div className="flex flex-nowrap items-center justify-between">
      <span className="text-sm text-muted-foreground">
        Comparing #{left} → #{right}
        {right === headRev && (
          <span className="text-xs text-muted-foreground"> (HEAD)</span>
        )}
      </span>
      <button
        type="button"
        onClick={onClose}
        className="text-xs text-theia-400 hover:underline"
      >
        Stop comparing
      </button>
    </div>
  );

  let body: React.ReactNode;
  if (isText) {
    body = <TextDiff depotPath={depotPath} leftRev={left} rightRev={right} />;
  } else if (IMAGE_EXTS.has(ext)) {
    body = (
      <ImageOverlayDiff
        depotPath={depotPath}
        leftRev={left}
        rightRev={right}
        source="raw"
      />
    );
  } else if (THREED_EXTS.has(ext)) {
    body = <MeshDiff depotPath={depotPath} leftRev={left} rightRev={right} />;
  } else if (ext === 'uasset') {
    body = (
      <UassetDiff
        depotPath={depotPath}
        leftRev={left}
        rightRev={right}
        filename={filename}
        fileSize={fileSize}
      />
    );
  } else if (isDamPreviewable) {
    body = (
      <SideBySidePreview
        depotPath={depotPath}
        leftRev={left}
        rightRev={right}
        filename={filename}
        fileSize={fileSize}
      />
    );
  } else {
    body = (
      <p className="text-sm text-muted-foreground">
        No diff renderer for this asset type yet. Download each
        revision separately to compare.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {header}
      {body}
    </div>
  );
}

function UassetDiff({
  depotPath,
  leftRev,
  rightRev,
  filename,
  fileSize,
}: {
  depotPath: string;
  leftRev: number;
  rightRev: number;
  filename: string;
  fileSize: number | null;
}) {
  const leftMeta = useQuery({
    queryKey: ['dam.meta', depotPath, leftRev],
    queryFn: () => fetchMeta(depotPath, leftRev),
  });
  const rightMeta = useQuery({
    queryKey: ['dam.meta', depotPath, rightRev],
    queryFn: () => fetchMeta(depotPath, rightRev),
  });

  if (leftMeta.isLoading || rightMeta.isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Spinner />
        <span className="text-sm text-muted-foreground">Resolving asset previews…</span>
      </div>
    );
  }

  const leftKind = previewKindOf(leftMeta.data);
  const rightKind = previewKindOf(rightMeta.data);
  const kindRank: Record<string, number> = {
    graph: 4, '3d': 3, image: 2, properties: 1, none: 0,
  };
  const kind =
    (kindRank[rightKind] ?? 0) >= (kindRank[leftKind] ?? 0) ? rightKind : leftKind;

  // Materials render with previewKind='image' (they carry an embedded
  // thumbnail), which would route to the image-only diff and hide the
  // expression-graph diff entirely. But a material has BOTH a preview
  // image AND a node graph (in its properties JSON), so offer all three:
  // Preview / Graph / Properties. Detected by asset class rather than
  // previewKind. (MaterialInstance has no expression graph — the Graph
  // tab self-reports "no parsable material graph", still gives Preview +
  // Properties.)
  const assetClass = assetClassOf(rightMeta.data) ?? assetClassOf(leftMeta.data) ?? '';
  if (assetClass.startsWith('Material')) {
    return (
      <div className="flex flex-col gap-2">
        {/* Name-matched parameter-override summary, above the tabs. Material
            INSTANCES carry Scalar/Vector/Texture parameter arrays; self-hides
            for base materials (which have no overrides — the Graph tab diffs
            those instead). */}
        <MaterialParamDiff depotPath={depotPath} leftRev={leftRev} rightRev={rightRev} />
        <Tabs defaultValue="graph">
        <TabsList>
          <TabsTrigger value="graph">Graph</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
          <TabsTrigger value="properties">Properties</TabsTrigger>
        </TabsList>
        <TabsContent value="graph">
          <Suspense fallback={<div className="p-4"><Spinner /></div>}>
            <MaterialGraphDiff depotPath={depotPath} leftRev={leftRev} rightRev={rightRev} />
          </Suspense>
        </TabsContent>
        <TabsContent value="preview">
          <ImageOverlayDiff depotPath={depotPath} leftRev={leftRev} rightRev={rightRev} source="preview" />
        </TabsContent>
        <TabsContent value="properties">
          <JsonPropertyDiff depotPath={depotPath} leftRev={leftRev} rightRev={rightRev} />
        </TabsContent>
        </Tabs>
      </div>
    );
  }

  if (kind === 'image') {
    return (
      <ImageOverlayDiff
        depotPath={depotPath}
        leftRev={leftRev}
        rightRev={rightRev}
        source="preview"
      />
    );
  }
  if (kind === '3d') {
    return <MeshDiff depotPath={depotPath} leftRev={leftRev} rightRev={rightRev} />;
  }
  if (kind === 'graph') {
    // Blueprint asset: structural graph diff (GUID-matched nodes) is the
    // primary view; the raw-JSON property tree is the fallback tab.
    return (
      <Tabs defaultValue="graph">
        <TabsList>
          <TabsTrigger value="graph">Graph</TabsTrigger>
          <TabsTrigger value="properties">JSON</TabsTrigger>
        </TabsList>
        <TabsContent value="graph">
          <Suspense fallback={<div className="p-4"><Spinner /></div>}>
            <BlueprintGraphDiff depotPath={depotPath} leftRev={leftRev} rightRev={rightRev} />
          </Suspense>
        </TabsContent>
        <TabsContent value="properties">
          <JsonPropertyDiff depotPath={depotPath} leftRev={leftRev} rightRev={rightRev} />
        </TabsContent>
      </Tabs>
    );
  }
  if (kind === 'properties') {
    return (
      <Tabs defaultValue="graph">
        <TabsList>
          <TabsTrigger value="graph">Graph</TabsTrigger>
          <TabsTrigger value="properties">Properties</TabsTrigger>
        </TabsList>
        <TabsContent value="graph">
          <Suspense fallback={<div className="p-4"><Spinner /></div>}>
            <MaterialGraphDiff
              depotPath={depotPath}
              leftRev={leftRev}
              rightRev={rightRev}
            />
          </Suspense>
        </TabsContent>
        <TabsContent value="properties">
          <JsonPropertyDiff
            depotPath={depotPath}
            leftRev={leftRev}
            rightRev={rightRev}
          />
        </TabsContent>
      </Tabs>
    );
  }
  return (
    <SideBySidePreview
      depotPath={depotPath}
      leftRev={leftRev}
      rightRev={rightRev}
      filename={filename}
      fileSize={fileSize}
    />
  );
}

async function fetchMeta(path: string, rev: number): Promise<Record<string, unknown> | null> {
  const res = await fetch(
    `/api/dam/meta?path=${encodeURIComponent(path)}&rev=${rev}`,
    { credentials: 'include' },
  );
  if (res.status !== 200) return null;
  return (await res.json()) as Record<string, unknown>;
}

function assetClassOf(meta: Record<string, unknown> | null | undefined): string | null {
  if (!meta) return null;
  const c = meta.assetClass;
  return typeof c === 'string' ? c : null;
}

function previewKindOf(meta: Record<string, unknown> | null | undefined): string {
  if (!meta) return 'none';
  if (meta.noPreview === true) return 'none';
  const pk = meta.previewKind;
  if (typeof pk === 'string') return pk;
  if (meta.hasMeshPreview === true) return '3d';
  if (meta.hasPreviewImage === true) return 'image';
  if (meta.hasProperties === true) return 'properties';
  return 'none';
}

function SideBySidePreview({
  depotPath,
  leftRev,
  rightRev,
  filename,
  fileSize,
}: {
  depotPath: string;
  leftRev: number;
  rightRev: number;
  filename: string;
  fileSize: number | null;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="flex flex-col gap-1">
        <span className="text-center text-xs text-muted-foreground">#{leftRev}</span>
        <AssetPreview
          depotPath={depotPath}
          headRev={leftRev}
          filename={`${filename} (#${leftRev})`}
          fileSize={fileSize}
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-center text-xs text-muted-foreground">#{rightRev}</span>
        <AssetPreview
          depotPath={depotPath}
          headRev={rightRev}
          filename={`${filename} (#${rightRev})`}
          fileSize={fileSize}
        />
      </div>
    </div>
  );
}
